/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { mipServicing, MipServicingResponse } from '../common/queryFunctions';
import { useAuthContext } from '../contexts/AuthContext';
import { useViewingContext } from '../contexts/ViewingContext';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { FormEvent, useCallback, useRef, useState } from 'react';
import DefaultModal from './modals/DefaultModal';
import css from '../css/MipServicing.module.css';
import { isStrEmptyOrNull } from '../common/UtilityFunctions';
import { borrowerNoAccessMsg } from '../common/constants';

interface MipServicingRequestData {
  opType: string;
  formData: FormData;
}
interface FormInput {
  opType: string;
  manualPaymentFile: File;
}
//SQC-647 Jacques Bega
//this compnent houses the form utilized by the HUD MIP servicing UI page
const MipServicing: React.FC = () => {
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();

  //state variables
  const abortControllerRef = useRef<AbortController | null>(null);
  const [formInput, setFormInput] = useState<FormInput>({
    opType: '',
    manualPaymentFile: null,
  });

  const mipServicingMutation: UseMutationResult<MipServicingResponse, Error, MipServicingRequestData, unknown> =
    useMutation({
      mutationFn: (requestData: MipServicingRequestData) => {
        const controller: AbortController = new AbortController();
        abortControllerRef.current = controller;
        const signal: AbortSignal = controller.signal;
        const mutationPromise: Promise<MipServicingResponse> = mipServicing(
          signal,
          token,
          requestData.opType,
          requestData.formData,
        );
        (mutationPromise as any).cancel = () => controller.abort();
        return mutationPromise;
      },
    });

  const clearFormInput = useCallback(() => {
    setFormInput({ opType: '', manualPaymentFile: null });
  }, []);

  const resetMutations = useCallback(() => {
    mipServicingMutation.reset();
  }, [mipServicingMutation]);

  const reset = useCallback(() => {
    clearFormInput();
    resetMutations();
  }, [clearFormInput, resetMutations]);

  const StatusModal = () => {
    const mutationErrorsPresent: boolean = mipServicingMutation.status === 'error';
    const mutationSuccessPresent: boolean = mipServicingMutation.status === 'success';
    const mutationPending: boolean = mipServicingMutation.status === 'pending';

    let titleStr: string = '';
    if (mutationErrorsPresent) titleStr = 'Error';
    else if (mutationSuccessPresent) titleStr = 'Success';
    else if (mutationPending) titleStr = 'Processing...';

    return (
      <DefaultModal
        open={mutationErrorsPresent || mutationSuccessPresent || mutationPending}
        title={<div className={css.modalTitle}>{titleStr}</div>}
        content={
          <div className={css.modalContent}>
            {mipServicingMutation.status === 'error' && <div>{mipServicingMutation.error.message}</div>}
            {mutationSuccessPresent && <div>{mipServicingMutation.data.responseData.response}</div>}
          </div>
        }
        footer={
          (mutationErrorsPresent || mutationSuccessPresent) && (
            <button className={css.modalButton} onClick={reset}>
              {'OK'}
            </button>
          )
        }
      ></DefaultModal>
    );
  };

  const updateFormInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const name: string = event.target.name;
    let value: string | File | null;
    if (event.target.type === 'file') {
      value = event.target.files?.[0] ?? null;
    } else {
      value = event.target.value;
    }

    console.log(name);
    console.log(value);

    setFormInput((prevFormInput) => {
      const newFormInput: FormInput = { ...prevFormInput };
      newFormInput[name] = value;
      if (name !== 'manualPaymentFile') newFormInput['manualPaymentFile'] = null; //clear upload file when an operation is selected
      return newFormInput;
    });
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      mipServicingMutation.reset();
      console.log(formInput);
      if (isStrEmptyOrNull(formInput.opType)) {
        alert('Operation must be selected');
        return;
      }
      if (formInput.opType === 'manualpayment' && !formInput.manualPaymentFile) {
        alert('No manual payment file provided.');
        return;
      }
      console.log('upload file details');
      console.log(formInput.manualPaymentFile);
      if (
        formInput.opType === 'manualpayment' &&
        formInput.manualPaymentFile.type !== 'text/csv' &&
        !formInput.manualPaymentFile.name.endsWith('.csv')
      ) {
        alert('Manual payment file provided is not a valid csv file.');
        setFormInput((prev) => ({ ...prev, manualPaymentFile: null })); //clear invalid file input
        return;
      }

      const requestdata: MipServicingRequestData = { opType: '', formData: new FormData() };
      requestdata.opType = formInput.opType;
      const metadataBlob = new Blob([JSON.stringify({})], { type: 'application/json' }); //blob is used here to assert json content type
      requestdata.formData.append('metadata', metadataBlob); //metadata is required even it there is no information
      if (formInput.opType === 'manualpayment') {
        requestdata.formData.append('manualPaymentFile', formInput.manualPaymentFile);
      }

      console.log('FORM DATA OF REQUEST');
      console.log([...requestdata.formData.entries()]);
      mipServicingMutation.mutate(requestdata);
    },
    [formInput, mipServicingMutation],
  );

  if (!isServicerView) return <div className='access-error-msg'>{borrowerNoAccessMsg}</div>;
  return (
    <>
      <form className={css.mipServicingForm} onSubmit={handleSubmit}>
        <div className={css.operationTypeFieldSet}>
          <span>Select Operation:</span>
          <label>
            <input
              type='radio'
              name='opType'
              value='updateadvreport'
              checked={formInput.opType === 'updateadvreport'}
              onChange={updateFormInput}
            ></input>
            Generate Updated HUD MIP Advance Notice Report
          </label>
          <label>
            <input
              type='radio'
              name='opType'
              value='updatebillreport'
              checked={formInput.opType === 'updatebillreport'}
              onChange={updateFormInput}
            ></input>
            Generate Updated HUD MIP Billing Report
          </label>
          <label>
            <input
              type='radio'
              name='opType'
              value='payment'
              checked={formInput.opType === 'payment'}
              onChange={updateFormInput}
            ></input>
            Send HUD MIP Payment File
          </label>
          <label>
            <input
              type='radio'
              name='opType'
              value='manualpayment'
              checked={formInput.opType === 'manualpayment'}
              onChange={updateFormInput}
            ></input>
            Send HUD MIP Manual Payments
          </label>
          {formInput.opType === 'manualpayment' && (
            <div>
              <input type='file' name='manualPaymentFile' accept='.csv,text/csv' onChange={updateFormInput}></input>
            </div>
          )}
        </div>
        <button className={css.mipServicingFormSubmitButton} type='submit'>
          Submit
        </button>
      </form>
      {StatusModal()}
    </>
  );
};

export default MipServicing;
